import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import ScreenShareRecorder from '../components/video-recorder/ScreenShareRecorder';
import VideoGalleryComponent from '../components/video-recorder/VideoGalleryComponent';
import { toast } from 'react-hot-toast';

enum VideoRecorderPageMode {
  GALLERY = 'gallery',
  RECORDER = 'recorder',
  SHARED_VIDEO = 'shared_video'
}

// Update VideoGalleryComponent to accept sharedVideoId prop
interface VideoGalleryComponentProps {
  onRecordNew: () => void;
  sharedVideoId?: string;
}

const VideoRecorderPage: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const { videoId } = useParams<{ videoId?: string }>();
  
  // State management with proper auth checks
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [mode, setMode] = useState<VideoRecorderPageMode>(
    videoId ? VideoRecorderPageMode.SHARED_VIDEO : VideoRecorderPageMode.GALLERY
  );
  const [isSharedVideoAccessible, setIsSharedVideoAccessible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Premium check with proper Supabase integration
  useEffect(() => {
    const checkPremiumStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Check user's subscription status from Supabase
        const { data: subscription, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking subscription:', error);
          toast.error('Error checking subscription status');
          setIsPremium(false);
        } else {
          setIsPremium(!!subscription);
        }
      } catch (error) {
        console.error('Error in premium check:', error);
        setIsPremium(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPremiumStatus();
  }, [user]);
  
  // Log mode changes - this needs to be at the top level before any early returns
  useEffect(() => {
    console.log('Current mode:', mode);
  }, [mode]);
  
  // Shared video access check with proper validation
  useEffect(() => {
    const checkSharedVideoAccess = async () => {
      if (!videoId) return;

      try {
        setIsLoading(true);
        
        // Check if video exists and is accessible
        const { data: videoData, error: videoError } = await supabase
          .from('video_recordings')
          .select('*')
          .eq('id', videoId)
          .single();

        if (videoError) {
          console.error('Error fetching video:', videoError);
          toast.error('Video not found or access denied');
          setIsSharedVideoAccessible(false);
          navigate('/video');
          return;
        }

        // Check if user has access through shares or ownership
        if (user && videoData.user_id === user.id) {
          setIsSharedVideoAccessible(true);
        } else if (videoData.is_public) {
          setIsSharedVideoAccessible(true);
        } else if (user) {
          // Check if video is shared with this user
          const { data: shareData, error: shareError } = await supabase
            .from('video_shares')
            .select('*')
            .eq('video_id', videoId)
            .eq('is_active', true)
            .or(`shared_with_user_id.eq.${user.id},shared_with_email.eq.${user.email}`)
            .single();

          if (!shareError && shareData) {
            // Check if share is still valid
            const isExpired = shareData.expires_at && new Date(shareData.expires_at) < new Date();
            setIsSharedVideoAccessible(!isExpired);
          } else {
            setIsSharedVideoAccessible(false);
            toast.error('You do not have permission to view this video');
            navigate('/video');
          }
        } else {
          setIsSharedVideoAccessible(false);
          toast.error('Please log in to view this video');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking video access:', error);
        setIsSharedVideoAccessible(false);
        toast.error('Error checking video access');
        navigate('/video');
      } finally {
        setIsLoading(false);
      }
    };

    checkSharedVideoAccess();
  }, [videoId, user, navigate]);

  // Authentication check
  useEffect(() => {
    if (!user && mode !== VideoRecorderPageMode.SHARED_VIDEO) {
      toast.error('Please log in to access the video recorder');
      navigate('/login');
    }
  }, [user, mode, navigate]);

  // Premium access check for recording
  useEffect(() => {
    if (user && mode === VideoRecorderPageMode.RECORDER && !isPremium && !isLoading) {
      toast.error('Video recording requires a premium subscription');
      navigate('/upgrade');
    }
  }, [user, mode, isPremium, isLoading, navigate]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // For shared videos, if not accessible, show error
  if (videoId && !isSharedVideoAccessible && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-10 text-center">
        <h2 className="text-2xl font-bold mb-4">Video Not Found</h2>
        <p className="mb-6">The video you're trying to access doesn't exist or you don't have permission to view it.</p>
        <button
          onClick={() => navigate('/marketing/video-recorder')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Go to My Videos
        </button>
      </div>
    );
  }

  // Handlers
  const handleRecordingComplete = (videoId: string) => {
    toast.success('Video recorded and saved successfully!');
    setMode(VideoRecorderPageMode.GALLERY);
  };

  const handleCancelRecording = () => {
    setMode(VideoRecorderPageMode.GALLERY);
  };
  
  
  const handleRecordNew = () => {
    if (!user) {
      toast.error('Please log in to record videos');
      navigate('/login');
      return;
    }
    
    if (!isPremium) {
      toast.error('Video recording requires a premium subscription');
      navigate('/upgrade');
      return;
    }
    
    console.log('Setting mode to RECORDER');
    setMode(VideoRecorderPageMode.RECORDER);
  };
  
  // We'll render premium gate conditionally instead of using early return to maintain hook order

  return (
    <div className="container mx-auto py-6 px-4 max-w-screen-2xl">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800 inline-flex items-center gap-2">
            {mode === VideoRecorderPageMode.GALLERY && 'Video Recorder'}
            {mode === VideoRecorderPageMode.RECORDER && 'Record New Video'}
            {mode === VideoRecorderPageMode.SHARED_VIDEO && 'Shared Video'}
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">Beta</span>
          </h1>
          
          {mode === VideoRecorderPageMode.GALLERY && (
            <button
              onClick={handleRecordNew}
              className="mt-3 sm:mt-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
              Record New Video
            </button>
          )}
          
          {(mode === VideoRecorderPageMode.RECORDER || mode === VideoRecorderPageMode.SHARED_VIDEO) && (
            <button
              onClick={() => setMode(VideoRecorderPageMode.GALLERY)}
              className="mt-3 sm:mt-0 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md shadow flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd" />
              </svg>
              {mode === VideoRecorderPageMode.SHARED_VIDEO ? 'Back to My Videos' : 'Back to Gallery'}
            </button>
          )}
        </div>
        
        <div className="text-gray-600">
          {mode === VideoRecorderPageMode.GALLERY && 'Record, store, and share videos with your team and clients.'}
          {mode === VideoRecorderPageMode.RECORDER && 'Record your screen, camera, or both to create a new video.'}
          {mode === VideoRecorderPageMode.SHARED_VIDEO && 'Viewing a shared video.'}
        </div>
        <p className="mt-2 text-xs text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">This feature is in Beta. Recording limits and UI may change.</p>
      </div>

      {/* Video Gallery Mode */}
      {mode === VideoRecorderPageMode.GALLERY && (
        <VideoGalleryComponent 
          onRecordNew={handleRecordNew}
        />
      )}
      
      {/* Recording Mode - Premium Only */}
      {mode === VideoRecorderPageMode.RECORDER && isPremium && (
        <>
          {console.log('Rendering ScreenShareRecorder, mode:', mode, 'isPremium:', isPremium)}
          <ScreenShareRecorder
            onVideoSaved={handleRecordingComplete}
            onCancel={handleCancelRecording}
            maxDurationSeconds={300}
          />
        </>
      )}
      
      {/* Premium Gate UI */}
      {mode === VideoRecorderPageMode.RECORDER && !isPremium && (
        <div className="flex flex-col items-center justify-center p-10 text-center">
          <h2 className="text-2xl font-bold mb-4">Premium Feature</h2>
          <p className="mb-6">Recording videos is a premium feature. Upgrade to access this tool.</p>
          <button
            onClick={() => navigate('/pricing')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Upgrade Now
          </button>
        </div>
      )}
      
      {/* Shared Video Mode */}
      {mode === VideoRecorderPageMode.SHARED_VIDEO && videoId && (
        <div className="bg-white shadow rounded-lg p-6">
          {/* Render video player for the shared video using videoId */}
          <div className="aspect-w-16 aspect-h-9 mb-4">
            <VideoGalleryComponent 
              onRecordNew={handleRecordNew}
              sharedVideoId={videoId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoRecorderPage;
